import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/utils";
import { Check, CornerDownLeft, Pencil, X } from "lucide-react";
import { motion } from "motion/react";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  askStepperReducer,
  collectAnswers,
  formatAskAnswers,
  initAskStepper,
  type AskQuestion,
  type AskStepperState,
} from "./harnessAskUserQuestion";

// The "Other" row is always appended to a question's options (Claude Code adds
// it implicitly; the payload never carries it). Its index in the rendered list
// is options.length.
const OTHER_INDEX = (q: AskQuestion) => q.options.length;

function selectedLabels(state: AskStepperState, i: number): string[] {
  const s = state.selections[i];
  return s?.kind === "options" ? s.labels : [];
}

/**
 * The AskUserQuestion panel — Claude Code's step-by-step question UI in our
 * design language. Rises from the composer area and, while a question is
 * pending, is the input surface. Keyboard-first: ↑/↓ move, Enter selects/
 * advances, 1–9 pick an option, Esc steps back (and dismisses from the first
 * question into the normal composer). Single-select advances on choice;
 * multi-select toggles with an explicit Next; a review step appears only when a
 * choice needed thought (multi-select or Other).
 */
export function HarnessAskPanel({
  questions,
  onSubmit,
  onDismiss,
}: {
  questions: AskQuestion[];
  /** Called with the composed answer message once every question is answered.
   *  May return a promise that rejects if the send fails — the panel then shows
   *  a retry rather than latching shut. */
  onSubmit: (message: string) => void | Promise<void>;
  /** "Type your own reply" / Esc-from-first — hand control back to the composer. */
  onDismiss: () => void;
}) {
  const [state, dispatch] = useReducer(
    askStepperReducer,
    questions,
    initAskStepper,
  );
  const [cursor, setCursor] = useState(0); // highlighted row within the current question
  const [otherText, setOtherText] = useState("");
  const otherRef = useRef<HTMLTextAreaElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // The submit lifecycle, entered once the stepper settles on "done". `null`
  // while the user is still answering; "sending" while an onSubmit is in flight;
  // "failed" after it rejects (offering a retry). The generation supersedes a
  // stale send: only the latest attempt may settle the status, and an edit (or
  // a new send) bumps it so an earlier promise's resolution is ignored.
  const [submitStatus, setSubmitStatus] = useState<"sending" | "failed" | null>(
    null,
  );
  const submitGenRef = useRef(0);

  const q = state.questions[state.current];
  const single = q && !q.multiSelect;

  // The panel is in its submit lifecycle once the stepper is done. Question
  // input (options, keyboard nav, chips-as-edit) is disabled while the answer
  // is in flight so it can't mutate what is being (or about to be) sent — the
  // two user messages can-never-both-land invariant.
  const submitting = submitStatus === "sending";
  const submitFailed = submitStatus === "failed";
  const inSubmitPhase = state.phase === "done";

  // Whether the current question already has an Other answer (drives the
  // "Update" label and the text sync). The field's visibility is owned solely
  // by `state.otherOpen`, which the reducer sets true on revisiting an
  // Other-answered question — so Esc/Cancel/Back reason about one flag.
  const currentSel = state.selections[state.current];
  const otherAnswered = currentSel?.kind === "other";
  const showOtherField = !!q && state.otherOpen;

  const doSubmit = useCallback(() => {
    const gen = ++submitGenRef.current;
    setSubmitStatus("sending");
    Promise.resolve(onSubmit(formatAskAnswers(collectAnswers(state)))).catch(
      () => {
        // Ignore a stale rejection: an edit or a newer send moved past this one.
        if (submitGenRef.current === gen) setSubmitStatus("failed");
      },
    );
  }, [onSubmit, state]);

  // Auto-submit the first time the stepper settles on "done". Guarded on
  // `submitStatus === null` so it fires once per settle, never while a send is
  // already in flight or has failed (the retry is explicit).
  useEffect(() => {
    if (inSubmitPhase && submitStatus === null) doSubmit();
  }, [inSubmitPhase, submitStatus, doSubmit]);

  // Leaving "done" (an edit via a chip/Back once a send has failed) supersedes
  // any in-flight send and drops the status, so the next settle submits afresh.
  useEffect(() => {
    if (!inSubmitPhase) {
      submitGenRef.current++;
      setSubmitStatus(null);
    }
  }, [inSubmitPhase]);

  // On landing on a question, sync the row cursor and the Other text field to
  // the answer already given: the cursor sits on the Other row (and the field
  // carries the previous text, editable) for an Other-answered question, on the
  // first option otherwise.
  useEffect(() => {
    const sel = state.selections[state.current];
    setCursor(sel?.kind === "other" && q ? OTHER_INDEX(q) : 0);
    setOtherText(sel?.kind === "other" ? sel.text : "");
    // Only re-sync on navigation, not on every selection edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.current, state.phase]);

  // Focus the Other field whenever it is shown (opened or revisited); otherwise
  // keep focus on the panel so the keyboard map works without a click.
  useEffect(() => {
    if (showOtherField) otherRef.current?.focus();
    else rootRef.current?.focus();
  }, [showOtherField, state.current, state.phase]);

  const rowCount = q ? q.options.length + 1 : 0; // options + Other

  const choose = useCallback(
    (i: number) => {
      if (!q) return;
      if (i === OTHER_INDEX(q)) {
        dispatch({ type: "openOther" });
      } else {
        dispatch({ type: "toggleOption", label: q.options[i].label });
      }
    },
    [q],
  );

  const submitOther = () => {
    if (otherText.trim()) dispatch({ type: "submitOther", text: otherText });
  };

  // Esc, everywhere: a fresh Other field collapses to the options; otherwise
  // step back a question, dismissing to the composer from the first one. A
  // committed Other field is the answer, so it steps back like any selection.
  const handleEscape = () => {
    if (state.otherOpen && currentSel?.kind !== "other") {
      dispatch({ type: "back" });
    } else if (state.phase !== "review" && state.current === 0) {
      onDismiss();
    } else {
      dispatch({ type: "back" });
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (inSubmitPhase) {
      // In the submit lifecycle the question is no longer interactive: Enter
      // retries a failed send, everything else (digits, arrows, space) is
      // ignored so a keystroke can't silently rewrite the answer being sent.
      if (submitFailed && e.key === "Enter") {
        e.preventDefault();
        doSubmit();
      }
      return;
    }
    if (state.phase === "review") {
      if (e.key === "Enter") {
        e.preventDefault();
        dispatch({ type: "confirm" });
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleEscape();
      }
      return;
    }
    if (showOtherField) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        submitOther();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleEscape();
      }
      return;
    }
    if (!q) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setCursor((c) => (c + 1) % rowCount);
        break;
      case "ArrowUp":
        e.preventDefault();
        setCursor((c) => (c - 1 + rowCount) % rowCount);
        break;
      case " ":
        // Space toggles the cursor option (the toggle key for multi-select;
        // harmless on single-select, where it also picks + advances).
        e.preventDefault();
        choose(cursor);
        break;
      case "Enter":
        e.preventDefault();
        if (q.multiSelect && cursor !== OTHER_INDEX(q)) {
          // Multi-select: toggling is Space/number keys; Enter confirms and
          // advances (a no-op until at least one option is chosen).
          dispatch({ type: "next" });
        } else {
          choose(cursor);
        }
        break;
      case "Escape":
        e.preventDefault();
        handleEscape();
        break;
      default: {
        const n = Number(e.key);
        if (Number.isInteger(n) && n >= 1 && n <= q.options.length) {
          e.preventDefault();
          setCursor(n - 1);
          choose(n - 1);
        }
      }
    }
  };

  const answers = useMemo(() => collectAnswers(state), [state]);

  return (
    <motion.div
      ref={rootRef}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, ease: [0.2, 0, 0, 1] }}
      data-testid="harness-ask-panel"
      className="w-full rounded-2xl border border-n-20 bg-card p-4 shadow-hairline outline-none"
    >
      {/* Progress chips, one per question. */}
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        {state.questions.map((question, i) => {
          const answered = !!state.selections[i];
          const active = state.phase === "asking" && i === state.current;
          return (
            <button
              key={i}
              type="button"
              // Editing is locked while a send is in flight, so a chip can't
              // start a second, racing submission; a failed send re-opens them.
              disabled={submitting}
              onClick={() => dispatch({ type: "goto", index: i })}
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-sans text-xs transition-colors",
                active
                  ? "bg-rust/12 font-medium text-rust"
                  : answered
                    ? "bg-n-10 text-n-70"
                    : "bg-n-05 text-n-40",
                submitting && "cursor-not-allowed opacity-60",
              )}
            >
              {answered && <Check className="size-3" />}
              {question.header}
            </button>
          );
        })}
        <span className="ml-auto flex items-center gap-2">
          <span className="hidden font-sans text-[11px] text-n-40 sm:inline">
            {state.phase === "review" ? "Enter to send" : "↑↓ · Enter · Esc"}
          </span>
          <button
            type="button"
            // Locked while a send is in flight — bailing to the composer then
            // would leave the answer to land and let a typed reply land too.
            disabled={submitting}
            onClick={onDismiss}
            title="Type your own reply"
            className={cn(
              "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-sans text-[11px] text-n-50 transition-colors hover:bg-n-10 hover:text-n-80",
              submitting && "cursor-not-allowed opacity-60",
            )}
          >
            <X className="size-3" />
            Type instead
          </button>
        </span>
      </div>

      {state.phase === "review" ? (
        <div>
          <div className="mb-2 font-sans text-sm font-medium text-n-90">
            Send these answers?
          </div>
          <div className="mb-3 space-y-1">
            {answers.map((a) => (
              <div key={a.header} className="flex gap-2 font-sans text-sm">
                <span className="shrink-0 text-n-50">{a.header}:</span>
                <span className="text-n-90">{a.values.join(", ") || "—"}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => dispatch({ type: "confirm" })}>
              Send <CornerDownLeft className="ml-1 size-3.5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => dispatch({ type: "back" })}
            >
              Back
            </Button>
          </div>
        </div>
      ) : state.phase === "done" ? (
        <div
          className="flex items-center gap-3 font-sans text-sm"
          data-testid="harness-ask-sending"
        >
          {submitFailed ? (
            <>
              <span className="text-lc-blocked">
                Couldn't send your answer.
              </span>
              <Button size="sm" onClick={doSubmit}>
                Send again <CornerDownLeft className="ml-1 size-3.5" />
              </Button>
              <span className="text-n-50">or pick a chip above to edit.</span>
            </>
          ) : (
            <span className="text-n-60">Sending your answer…</span>
          )}
        </div>
      ) : q ? (
        <div>
          <div className="mb-3 font-sans text-sm text-n-90 text-pretty">
            {q.question}
          </div>
          <div className="space-y-1.5" role="listbox">
            {q.options.map((opt, i) => {
              const chosen = selectedLabels(state, state.current).includes(
                opt.label,
              );
              const highlighted = !showOtherField && cursor === i;
              return (
                <button
                  key={opt.label}
                  type="button"
                  role="option"
                  aria-selected={chosen}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => choose(i)}
                  data-testid="harness-ask-option"
                  className={cn(
                    "flex w-full items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors",
                    highlighted
                      ? "border-rust/40 bg-rust/8"
                      : "border-n-20 bg-background hover:border-n-30",
                  )}
                >
                  <span
                    className={cn(
                      "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border",
                      chosen ? "border-rust bg-rust text-white" : "border-n-30",
                      q.multiSelect && !chosen && "rounded-[5px]",
                      q.multiSelect && chosen && "rounded-[5px]",
                    )}
                  >
                    {chosen && <Check className="size-3" strokeWidth={3} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline gap-2">
                      <span className="font-sans text-sm font-medium text-n-90">
                        {opt.label}
                      </span>
                      <span className="ml-auto shrink-0 font-mono text-[10px] text-n-40">
                        {i + 1}
                      </span>
                    </span>
                    {opt.description && (
                      <span className="mt-0.5 block font-sans text-xs text-n-50 text-pretty">
                        {opt.description}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}

            {/* Always-present "Other" escape into free text. Also shown
                pre-filled when revisiting an Other-answered question. */}
            {showOtherField ? (
              <div className="rounded-xl border border-rust/40 bg-rust/8 px-3 py-2.5">
                <Textarea
                  ref={otherRef}
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  placeholder="Type your own answer…"
                  rows={2}
                  className="min-h-0 resize-none border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0"
                />
                <div className="mt-2 flex items-center gap-2">
                  <Button
                    size="sm"
                    disabled={!otherText.trim()}
                    onClick={submitOther}
                  >
                    {otherAnswered ? "Update" : single ? "Answer" : "Add"}{" "}
                    <CornerDownLeft className="ml-1 size-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={handleEscape}>
                    {otherAnswered ? "Back" : "Cancel"}
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onMouseEnter={() => setCursor(q.options.length)}
                onClick={() => dispatch({ type: "openOther" })}
                data-testid="harness-ask-other"
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl border border-dashed px-3 py-2.5 text-left font-sans text-sm transition-colors",
                  !state.otherOpen && cursor === q.options.length
                    ? "border-rust/40 bg-rust/8 text-n-80"
                    : "border-n-30 text-n-60 hover:border-n-40",
                )}
              >
                <Pencil className="size-3.5 shrink-0" />
                Other…
              </button>
            )}
          </div>

          {/* Multi-select needs an explicit confirm. */}
          {q.multiSelect && !showOtherField && (
            <div className="mt-3">
              <Button
                size="sm"
                disabled={selectedLabels(state, state.current).length === 0}
                onClick={() => dispatch({ type: "next" })}
              >
                {state.current === state.questions.length - 1
                  ? "Review"
                  : "Next"}{" "}
                <CornerDownLeft className="ml-1 size-3.5" />
              </Button>
            </div>
          )}
        </div>
      ) : null}
    </motion.div>
  );
}
